import React, { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { AxiosError } from "axios";
import { createIngredient } from "../../api/CafeteriaClient";
import { AppContext } from "../../AppContextProvider";
import ConfirmDialog from "../ConfirmDialog";

interface ProductAutocompleteProps {
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const ProductAutocomplete: React.FC<ProductAutocompleteProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const { ingredients, setIngredients, setSnackbarErrorMsg } =
    useContext(AppContext);
  const [inputValue, setInputValue] = useState(value);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const options = ingredients
    .map((ingredient) => ingredient.name)
    .sort((a, b) => a.localeCompare(b));

  const trimmed = inputValue.trim();
  const hasExactMatch = options.some(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );
  const showAddButton =
    !disabled && trimmed.length > 0 && !hasExactMatch;

  const handleValueChanged = (newValue: string) => {
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    const nextTrimmed = newInputValue.trim();
    const existing = options.find(
      (option) => option.toLowerCase() === nextTrimmed.toLowerCase(),
    );

    if (existing && newInputValue === nextTrimmed) {
      handleValueChanged(existing);
    } else {
      handleValueChanged(newInputValue);
    }
  };

  const handleConfirmAdd = async () => {
    if (!trimmed) {
      return;
    }

    try {
      setIsAdding(true);
      const created = await createIngredient(trimmed);
      setIngredients([...ingredients, created]);
      handleValueChanged(created.name);
      setShowConfirmDialog(false);
    } catch (error) {
      const axiosError = error as AxiosError;
      setSnackbarErrorMsg(
        "Error creating product / ingredient: " +
          (axiosError.response?.data?.toString() ??
            axiosError.response?.statusText ??
            "Unknown server error"),
      );
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <Autocomplete
        freeSolo
        autoHighlight
        disableClearable
        disabled={disabled}
        options={options}
        value={inputValue}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={(_, newValue) => {
          handleValueChanged(
            typeof newValue === "string" ? newValue : (newValue ?? ""),
          );
        }}
        filterOptions={(opts, state) => {
          const query = state.inputValue.trim().toLowerCase();
          if (query === "") {
            return opts;
          }
          return opts.filter((option) =>
            option.toLowerCase().includes(query),
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps?.endAdornment}
                    {showAddButton ? (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setShowConfirmDialog(true)}
                        sx={{ flexShrink: 0, padding: 0 }}
                      >
                        <Add />
                      </IconButton>
                    ) : null}
                  </>
                ),
              },
            }}
          />
        )}
      />
      {showConfirmDialog && (
        <ConfirmDialog
          open={true}
          title="Add Product / Ingredient"
          onOk={handleConfirmAdd}
          onCancel={() => setShowConfirmDialog(false)}
          isOkDisabled={isAdding}
          okLabel="Add"
        >
          <Typography>
            {`"${trimmed}" will be added as a new product / ingredient. Do you wish to continue?`}
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};

export default ProductAutocomplete;
